import ResponsiveContainer from "../components/DensityResponsiveContainer";
import { layoutCoordinate, viewportHeight as layoutViewportHeight } from "../utils/displayDensity";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../components/dashboardTypography.css";
import { CalendarDays, Download, GalleryHorizontalEnd, Grip, Image as ImageIcon, RotateCcw, Slash, Table2, TrendingUp, Waves } from "lucide-react";
import { median } from "d3";
import TikTokChartTools from "../components/TikTokChartTools";
import MindshareHeaderControls from "../components/MindshareHeaderControls";
import useSeriesIntroAnimation from "../hooks/useSeriesIntroAnimation";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceDot,
  ReferenceLine,

  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import DateSlider from "../components/DateSlider";
import VirtualizedTable from "../components/VirtualizedTable";
import VirtualizedTikTokGrid from "../components/VirtualizedTikTokGrid";
import DashboardLoader from "../components/DashboardLoader";
import { useTikTokData } from "../contexts/TikTokDataContext";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import SavedViewsManager from "../components/SavedViewsManager";
import {
  loadTikTokHistory,
  rankTikTokRows,
  selectTikTokComparisonRows,
  tikTokGameGroupKey,
  tikTokHistoryKey,
} from "../services/tiktokData";
import { rankTikTokMomentum } from "../services/tiktokMomentum";
import CsvExportButton from "../components/CsvExportButton";
import "./tiktok-market.css";
import TikTokMarketMind from "../components/TikTokMarketMind";
import { MindshareChartSkeleton } from "./mindSHARE";
import { MGHeadingToggle, MGSectionHeader } from "../components/editorial/MGEditorial";
import TikTokViewExhibition, { TikTokViewfinderButton } from "../experiments/tiktokViewMode/TikTokViewExhibition";

const CARD_WIDTH = 200;
const GRID_COL_GAP = 12;
const TOTAL_LINE_COLOR = "#3fa9f5";
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
function TikTokMarketMindIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="10" r="5" /><circle cx="17" cy="8" r="3" opacity="0.65" /><circle cx="16" cy="16" r="4" opacity="0.8" /><circle cx="7" cy="18" r="2.5" opacity="0.6" /><circle cx="4" cy="4" r="2" opacity="0.7" /><circle cx="20" cy="3" r="1.5" opacity="0.6" /></svg>;
}

const TIKTOK_HEADER_VIEWS = [
  { mode: "cards", label: "Cards", Icon: GalleryHorizontalEnd },
  { mode: "table", label: "Table", Icon: Table2 },
  { mode: "market", label: "Market Mind", Icon: TikTokMarketMindIcon },
];

const seriesColor = (idx) =>
  ["#3fa9f5", "#F5E6C8", "#ff9b70", "#33c3eb", "#f1726a", "#66CCFF", "#A855F7", "#FF5342"][idx % 8];

const ymdToLocalDate = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toISODate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fmtAbbrev = (n) => {
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Math.round(n).toString();
};

const fmtAxisDateTime = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return value;
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, "0");
  const hh = d.getHours();
  return `${m}/${day} ${hh}:00`;
};

const fmtAxisDate = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return value;
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}`;
};

const fmtTooltipDateTime = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return String(value);
  return d.toLocaleString();
};

const fmtDailyDate = value => new Date(value).toLocaleDateString("en-US", {
  timeZone: "America/Los_Angeles", month: "numeric", day: "numeric",
});

const escapeSvgText = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const makeFallbackImage = (gameName) => {
  const words = String(gameName || "Unknown Game").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (test.length > 17 && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });

  if (line) lines.push(line);

  const safeLines = (lines.length ? lines : ["Unknown Game"])
    .slice(0, 5)
    .map(escapeSvgText);

  const lineHeight = 27;
  const centerY = 190;
  const startY = centerY - ((safeLines.length - 1) * lineHeight) / 2;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="285" height="380" viewBox="0 0 285 380">
      <rect width="285" height="380" fill="#050505"/>
      <rect x="4" y="4" width="277" height="372" rx="10" fill="#111" stroke="#3fa9f5" stroke-width="2"/>
      ${safeLines
        .map(
          (text, i) => `
            <text x="142.5" y="${startY + i * lineHeight}"
              text-anchor="middle"
              dominant-baseline="middle"
              fill="#ffffff"
              font-family="Jura, sans-serif"
              font-size="18"
              font-weight="800">
              ${text}
            </text>
          `
        )
        .join("")}
    </svg>
  `)}`;
};

const splitCsvField = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const numericValue = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const pickField = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return fallback;
};

const normalizeLookupKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");


const normalizeHistoryPayload = (payload) => {
  const rawPoints = Array.isArray(payload?.points) ? payload.points : [];

  return rawPoints
    .map((point) => {
      const ts = point.period_start || point.dt || point.panel_dt || point.source_dt;
      const d = new Date(ts);
      if (!ts || isNaN(d.getTime())) return null;

      return {
        ts,
        snapshotId: point.snapshot_id,
        isDaily: payload?.granularity === "daily_net",
        isPartial: point.is_partial === true,
        timeMs: d.getTime(),
        views:
          point.views == null || point.views === ""
            ? null
            : Number(point.views),
        videos:
          point.videos == null || point.videos === ""
            ? null
            : Number(point.videos),
        deltaViews:
          point.delta_views == null && point.deltaViews == null
            ? null
            : Number(point.delta_views ?? point.deltaViews ?? 0),
        deltaVideos:
          point.delta_videos == null && point.deltaVideos == null
            ? null
            : Number(point.delta_videos ?? point.deltaVideos ?? 0),
        sourceDt: point.source_dt || null,
	sourceDtPt: point.source_dt_pt || null,
	sourceTimeMs: point.source_dt ? new Date(point.source_dt).getTime() : d.getTime(),
	ageHours: Number(point.age_hours ?? 0) || 0,
	isStale: Number(point.is_stale ?? 0) || 0,
	isObserved: ["observed_daily", "observations", "daily_net"].includes(payload?.granularity),
	counterDecrease: !!point.counter_decrease,
	videoCounterDecrease: !!point.video_counter_decrease,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.timeMs - b.timeMs || a.snapshotId - b.snapshotId);
};

const buildRenderableHistory = (history, chartMode) => {
  if (!Array.isArray(history) || !history.length) return [];

  return history.map((row, idx) => {
    const prev = idx > 0 ? history[idx - 1] : null;

    const panelGapHours = prev ? (row.timeMs - prev.timeMs) / HOUR_MS : 0;

    const prevSourceMs = prev?.sourceTimeMs || prev?.timeMs;
    const rowSourceMs = row.sourceTimeMs || row.timeMs;
    const sourceGapHours = prev ? (rowSourceMs - prevSourceMs) / HOUR_MS : 0;

    const effectiveGapHours =
      sourceGapHours > 0 ? sourceGapHours : panelGapHours > 0 ? panelGapHours : null;

    const computedDeltaViews =
      !row.counterDecrease && prev &&
      row.views != null &&
      prev.views != null &&
      Number(row.views) >= Number(prev.views)
        ? Number(row.views) - Number(prev.views)
        : null;

    const computedDeltaVideos =
      !row.videoCounterDecrease && prev &&
      row.videos != null &&
      prev.videos != null &&
      Number(row.videos) >= Number(prev.videos)
        ? Number(row.videos) - Number(prev.videos)
        : null;

    const rawDeltaViews =
      row.deltaViews != null && Number.isFinite(Number(row.deltaViews))
        ? Number(row.deltaViews)
        : row.isDaily ? null : computedDeltaViews;

    const rawDeltaVideos =
      row.deltaVideos != null && Number.isFinite(Number(row.deltaVideos))
        ? Number(row.deltaVideos)
        : row.isDaily ? null : computedDeltaVideos;

    const viewsPerHour =
      rawDeltaViews != null && rawDeltaViews > 0 && effectiveGapHours
        ? rawDeltaViews / effectiveGapHours
        : null;

    const videosPerHour =
      rawDeltaVideos != null && rawDeltaVideos > 0 && effectiveGapHours
        ? rawDeltaVideos / effectiveGapHours
        : null;

    const isCatchup =
      !row.isObserved && chartMode !== "lifetime" &&
      rawDeltaViews != null &&
      rawDeltaViews > 1000000 &&
      (row.isStale === 1 ||
        row.ageHours > 6 ||
        panelGapHours > 6 ||
        sourceGapHours > 6);

    const resolvedRawDeltaViews =
      rawDeltaViews != null && (row.isDaily || rawDeltaViews >= 0) ? rawDeltaViews : null;

    const resolvedRawDeltaVideos =
      rawDeltaVideos != null && (row.isDaily || rawDeltaVideos >= 0) ? rawDeltaVideos : null;

    return {
      ...row,
      deltaViews: rawDeltaViews,
      deltaVideos: rawDeltaVideos,
      deltaViewsPerHour: viewsPerHour,
      deltaVideosPerHour: videosPerHour,
      isCatchup,
      panelGapHours,
      sourceGapHours,
      effectiveGapHours,
      chartValue:
        chartMode === "lifetime"
          ? row.views
          : isCatchup
            ? null
            : chartMode === "change"
              ? row.isObserved ? resolvedRawDeltaViews : viewsPerHour
              : resolvedRawDeltaViews,

      chartVideoValue:
        chartMode === "lifetime"
          ? row.videos
          : isCatchup
            ? null
            : chartMode === "change"
              ? row.isObserved ? resolvedRawDeltaVideos : videosPerHour
              : resolvedRawDeltaVideos,
    };
  });
};

const TikTokChartSelect = ({ value, onChange, options, ariaLabel, width = 168, icon = null }) => (
  <div style={{ position: "relative", width, height: 30, flexShrink: 0 }}>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={ariaLabel}
      style={{
        appearance: "none",
        WebkitAppearance: "none",
        width: "100%",
        height: "100%",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(255,255,255,0.03)",
        color: "#eef3f7",
        padding: icon ? "0 30px 0 34px" : "0 30px 0 10px",
        outline: "none",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 500,
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value} title={option.description}
          style={{ background: "#0a0a0a", color: "#fff" }}>
          {option.label}
        </option>
      ))}
    </select>
    {icon && <span aria-hidden style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", display: "inline-flex", pointerEvents: "none" }}>{icon}</span>}
    <span aria-hidden style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-52%)", color: "#fff", fontSize: 12, pointerEvents: "none" }}>▾</span>
  </div>
);

function TikTok({ experiment } = {}) {
  const [games, setGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [headerToolsTarget, setHeaderToolsTarget] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedDevelopers, setSelectedDevelopers] = useState([]);
  const [selectedLiveService, setSelectedLiveService] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [selectedUGC, setSelectedUGC] = useState(null);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [sortMode, setSortMode] = useState("views");
  const [rankingWindow, setRankingWindow] = useState("latest");
  const [onlyUnreleased, setOnlyUnreleased] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [comparisonGames, setComparisonGames] = useState([]);
  const [useLogScale, setUseLogScale] = useState(false);
  const [chartMode, setChartMode] = useState("change");
	// "change" | "lifetime" | "period"
  const [chartStyle, setChartStyle] = useState("area");
  const [chartBackground, setChartBackground] = useState("none");
  const [showAverage, setShowAverage] = useState(false);
  const [showMedian, setShowMedian] = useState(false);
  const [show24HourMovingAverage, setShow24HourMovingAverage] = useState(false);
  const [legendOpen, setLegendOpen] = useState(true);
  const [chartColors, setChartColors] = useState({});
  const [chartHeight, setChartHeight] = useState(420);
  const chartResizeRef = useRef(null);
  const [showTableView, setShowTableView] = useState(experiment?.requestedView === "table");
  const [viewMode, setViewMode] = useState(experiment?.requestedView || "cards");
  const choosingView = Boolean(experiment?.viewfinderOpen);
  const requestedExperimentalView = experiment?.requestedView;
  useEffect(() => {
    if (!requestedExperimentalView) return;
    setViewMode(requestedExperimentalView);
    setShowTableView(requestedExperimentalView === "table");
    setFiltersOpen(false);
  }, [requestedExperimentalView]);
  const [marketSplitView, setMarketSplitView] = useState(false);
  const [marketShape, setMarketShape] = useState("bubbles");
  const [marketSort, setMarketSort] = useState("views");
  const [marketFavorites, setMarketFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preparationError, setPreparationError] = useState(null);
  const [skeletonRowCount, setSkeletonRowCount] = useState(30);
  const [hiddenGames, setHiddenGames] = useState([]);
  const [growthWindow, setGrowthWindow] = useState("30d");
  const [highlighted, setHighlighted] = useState(() => new Set());
  const highlightSeries = (seriesKey, event) => {
    const multi = event.ctrlKey || event.metaKey;
    setHighlighted(previous => {
      const next = new Set(previous);
      if (multi) {
        if (next.has(seriesKey)) next.delete(seriesKey);
        else next.add(seriesKey);
      } else if (next.size === 1 && next.has(seriesKey)) next.clear();
      else { next.clear(); next.add(seriesKey); }
      return next;
    });
  };
  const [legendHidden, setLegendHidden] = useState(() => new Set());
  const getSeriesColor = (game, index) => chartColors[game.seriesKey] || seriesColor(index);
  const [historyCache, setHistoryCache] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});
  const [historyErrors, setHistoryErrors] = useState({});
  const [historyRetry, setHistoryRetry] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const previousComparisonGamesRef = useRef([]);

  // Steam/Twitch-style chart window scrubber for TikTok history.
  // These are array indexes into the fully prepared comparison chart data.
  const [ttWinStart, setTtWinStart] = useState(0);
  const [ttWinEnd, setTtWinEnd] = useState(0);
  const ttHasInitWindow = useRef(false);
  const ttPreviousDataLength = useRef(0);

  const {
    metaData,
    tikTokSummary,
    tikTokManifest,
    refreshTikTokData,
    getAllTags,
    getAllPublishers,
    getAllGenres,
    getAllDevelopers,
    getAllLiveServiceOptions,
    getAllPricingOptions,
    getAllUGCOptions,
    getAllFocusOptions,
    getAllPlatformGroups,
    getAllReleaseDates,
    allTags,
    allPublishers,
    allGenres,
    allDevelopers,
    allLiveService,
    allPricing,
    allUGC,
    allFocus,
    allPlatforms,
    releaseDateRange,
    loading: globalLoading,
    error: datasetError,
  } = useTikTokData();

  useEffect(() => {
    const findTarget = () => setHeaderToolsTarget(document.getElementById("tiktok-header-saved-controls"));
    findTarget();
    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

const tiktokSummaryFreshnessKey = useMemo(() => {
  if (!tikTokSummary?.length) return "";

  let latest = 0;

  for (const row of tikTokSummary) {
    const t = new Date(row.dt || row.dt_pt || 0).getTime();
    if (!Number.isNaN(t) && t > latest) latest = t;
  }

  return tikTokManifest?.revision || String(latest);
}, [tikTokSummary, tikTokManifest?.revision]);

useEffect(() => {
  refreshTikTokData();
}, []);

  useEffect(() => {
    const loadData = async () => {
      if (globalLoading) return;

      try {
        setPreparationError(null);
        const shouldShowLoader = !hasLoadedOnceRef.current;
  	if (shouldShowLoader) setLoading(true);

        const metaMap = new Map();
        const metaNormMap = new Map();
        for (const meta of metaData || []) {
          const trimmedGame = pickField(meta, ["Game", "game"]).trim();
          if (trimmedGame) {
            metaMap.set(trimmedGame, meta);
            metaNormMap.set(normalizeLookupKey(trimmedGame), meta);
          }
        }

        const todayLocal = new Date();
        todayLocal.setHours(0, 0, 0, 0);

        const mergedSummaryMap = new Map();

        (tikTokSummary || []).forEach((row) => {
          const configId = pickField(row, ["config_id", "Config_ID", "id", "ID"]);
          const game = String(pickField(row, ["game", "Game", "GAME"])).trim();
          const gameKey = String(
            pickField(row, ["game_key", "Game_Key", "gameKey", "GameKey"], game)
          ).trim() || game;
          const hashtag = String(pickField(row, ["hashtag", "Hashtag", "tag", "Tag"])).trim();
          const key = configId ? `config:${configId}` : `tag:${gameKey.toLowerCase()}::${hashtag.toLowerCase()}`;

          if (key && gameKey && hashtag) mergedSummaryMap.set(key, row);
        });

        const mergedTikTokSummary = Array.from(mergedSummaryMap.values());

        const rowItems = mergedTikTokSummary
          .map((row) => {
            const game = String(pickField(row, ["game", "Game", "GAME"])).trim();
            if (!game) return null;

            const gameKey = String(
              pickField(row, ["game_key", "Game_Key", "gameKey", "GameKey"], game)
            ).trim() || game;

            const meta =
              metaMap.get(gameKey) ||
              metaMap.get(game) ||
              metaNormMap.get(normalizeLookupKey(gameKey)) ||
              metaNormMap.get(normalizeLookupKey(game));

            const imageUrlFromMeta = pickField(meta, ["ImageURL", "imageUrl", "image_url"]);
            const hasMetadata = !!meta && !!imageUrlFromMeta;

            const rdStr = hasMetadata ? toISODate(pickField(meta, ["Release_Date", "release_date"])) : null;
            const rdLocal = rdStr ? ymdToLocalDate(rdStr) : null;
            const isUnreleased = rdLocal ? rdLocal > todayLocal : true;

            const configId = Number(pickField(row, ["config_id", "Config_ID", "id", "ID"])) || null;
            const hashtag = String(pickField(row, ["hashtag", "Hashtag", "tag", "Tag"])).trim();
            const displayName = hashtag ? `${gameKey} (#${hashtag})` : gameKey;
            const seriesKey = configId ? `tt_${configId}` : `${gameKey}-${hashtag}`;

            return {
              uid: String(configId || seriesKey),
              id: seriesKey,
              selectionKey: seriesKey,
              seriesKey,
              window_1d: row.window_1d,
              window_7d: row.window_7d,
              displayName,
              name: gameKey,
              gameKey,
              gameName: game,
              hasMetadata,
              config_id: configId,
              history_key:
                row.history_key?.trim() ||
                null,
              history_points: row.history_points,
              daily_history_key: row.daily_history_key,
              age_hours: row.age_hours,
              imageUrl: hasMetadata ? imageUrlFromMeta : makeFallbackImage(gameKey),
              publisher: hasMetadata ? String(pickField(meta, ["Publisher", "publisher"])).trim() || "" : "Unmatched TikTok Tracking",
              tags: hasMetadata ? splitCsvField(pickField(meta, ["Tags", "tags"])) : [],
              genres: hasMetadata ? splitCsvField(pickField(meta, ["Genre", "genre"])) : [],
              developer: hasMetadata ? String(pickField(meta, ["Developer", "developer"])).trim() || "" : "",
              live_service_static: hasMetadata ? String(pickField(meta, ["Live_Service_Static", "live_service_static"])).trim() || "" : "",
              pricing: hasMetadata ? String(pickField(meta, ["Pricing_Monetization", "pricing"])).trim() || "" : "",
              user_generated_content: hasMetadata
                ? String(pickField(meta, ["User_Generated_Content_(UGC)", "user_generated_content"])).trim() || ""
                : "",
              single_multiplayer_focus: hasMetadata
                ? String(pickField(meta, ["Single_Multiplayer_Focus", "single_multiplayer_focus"])).trim() || ""
                : "",
              platforms: hasMetadata ? splitCsvField(pickField(meta, ["Platform_Group_Filter", "platforms"])) : [],
              videos: numericValue(row.videos, row.Videos),
              views: numericValue(row.views, row.Views),
              videoGrowth_1d: numericValue(row.video_growth_1d, row.Video_Growth_1d, row.videos_1d, row.videos_daily_change),
              viewGrowth_1d: numericValue(row.view_growth_1d, row.View_Growth_1d, row.views_1d, row.views_daily_change),
              percentGrowth_1d: numericValue(row.percent_growth_1d, row.Percent_Growth_1d),
              videoGrowth_7d: numericValue(row.video_growth_7d, row.Video_Growth_7d, row.videos_7d),
              viewGrowth_7d: numericValue(row.view_growth_7d, row.View_Growth_7d, row.views_7d),
              percentGrowth_7d: numericValue(row.percent_growth_7d, row.Percent_Growth_7d),
              videoGrowth_30d: numericValue(row.video_growth_30d, row.Video_Growth_30d, row.videos_30d),
              viewGrowth_30d: numericValue(row.view_growth_30d, row.View_Growth_30d, row.views_30d),
              percentGrowth_30d: numericValue(row.percent_growth_30d, row.Percent_Growth_30d),
              hashtag,
              releaseStatus: hasMetadata ? pickField(meta, ["Released_Unreleased", "releaseStatus"]) || "" : "Unmatched",
              release_date: rdStr,
              isUnreleased,
              latest_dt: pickField(row, ["dt", "Dt", "latest_dt", "Latest_Dt"]) || null,
              latest_dt_pt: pickField(row, ["dt_pt", "Dt_Pt", "latest_dt_pt", "Latest_Dt_Pt"]) || null,
            };
          })
          .filter(Boolean);

        const groupSelectionKeys = new Map();
        const groupHashtags = new Map();
        rowItems.forEach(game => {
          const groupKey = tikTokGameGroupKey(game);
          if (!groupKey) return;
          const keys = groupSelectionKeys.get(groupKey) || [];
          keys.push(game.selectionKey);
          groupSelectionKeys.set(groupKey, keys);
          const hashtag = String(game.hashtag || "").trim().replace(/^#+/, "");
          if (!hashtag) return;
          const hashtags = groupHashtags.get(groupKey) || [];
          const existing = hashtags.find(tag => tag.value.toLowerCase() === hashtag.toLowerCase());
          if (existing) existing.views = Math.max(existing.views, Number(game.views) || 0);
          else hashtags.push({ value: hashtag, views: Number(game.views) || 0 });
          groupHashtags.set(groupKey, hashtags);
        });
        const rowItemsWithGroupKeys = rowItems.map(game => {
          const groupKey = tikTokGameGroupKey(game);
          return {
            ...game,
            groupSelectionKeys: groupSelectionKeys.get(groupKey) || [game.selectionKey],
            groupHashtags: (groupHashtags.get(groupKey) || [{ value: game.hashtag, views: game.views }])
              .slice()
              .sort((a, b) => b.views - a.views)
              .map(tag => tag.value),
          };
        });
        const momentumRanking = rankTikTokMomentum(rowItemsWithGroupKeys);
        const combined = momentumRanking.rows;

        if (typeof window !== "undefined") {
          window.__TIKTOK_SUMMARY = mergedTikTokSummary;
          window.__TIKTOK_ROW_ITEMS = rowItems;
          window.__TIKTOK_GAMES = combined;
          window.__TIKTOK_MOMENTUM_AUDIT = {
            sourceRows: rowItems.length,
            eligibleRows: momentumRanking.eligibleCount,
            thresholds: momentumRanking.thresholds,
            strongRankCutoff: momentumRanking.strongRankCutoff,
          };

          const debugNeedles = [
            "devil may cry 6",
            "bloodborne: the new blood",
            "infamous remake",
            "kingdom hearts 4",
            "god of war saga",
          ];

          console.log("TikTok debug globals ready", {
            summaryRows: mergedTikTokSummary.length,
            rowItems: rowItems.length,
            groupedGames: combined.length,
            debugRows: rowItems.filter((item) =>
              debugNeedles.some((needle) =>
                `${item.name} ${item.gameKey} ${item.displayName} ${item.hashtag}`
                  .toLowerCase()
                  .includes(needle)
              )
            ),
          });
        }

        setGames(combined);
	hasLoadedOnceRef.current = true;

        getAllTags();
        getAllPublishers();
        getAllGenres();
        getAllDevelopers();
        getAllLiveServiceOptions();
        getAllPricingOptions();
        getAllUGCOptions();
        getAllFocusOptions();
        getAllPlatformGroups();
        getAllReleaseDates();
      } catch (err) {
        console.error("❌ Failed to load TikTok summary or metadata:", err);
        setPreparationError(err?.message || "Failed to prepare TikTok data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    metaData,
    tikTokSummary,
    globalLoading,
  ]);

  const comparisonGameObjects = useMemo(
    () => selectTikTokComparisonRows(games, comparisonGames),
    [games, comparisonGames]
  );

  const toggleComparisonGame = game => {
    const groupKeys = game.groupSelectionKeys?.length
      ? game.groupSelectionKeys
      : [game.selectionKey || game.name].filter(Boolean);
    let added = false;
    setComparisonGames(previous => {
      const alreadySelected = groupKeys.some(key => previous.includes(key)) || previous.includes(game.name);
      if (alreadySelected) {
        return previous.filter(key => !groupKeys.includes(key) && key !== game.name);
      }
      added = true;
      return [...new Set([...previous, ...groupKeys])];
    });
    // Default the chart to Past Week whenever a game is added via "+".
    if (added) setGrowthWindow("7d");
    setShowInsights(true);
  };

  const removeComparisonGame = game => {
    const groupKey = tikTokGameGroupKey(game);
    const groupKeys = game.groupSelectionKeys?.length
      ? game.groupSelectionKeys
      : [game.selectionKey || game.name].filter(Boolean);
    const seriesKeys = comparisonGameObjects
      .filter(candidate => tikTokGameGroupKey(candidate) === groupKey)
      .map(candidate => candidate.seriesKey);
    setComparisonGames(previous => previous.filter(key => !groupKeys.includes(key) && key !== game.name));
    setHighlighted(previous => new Set([...previous].filter(key => !seriesKeys.includes(key))));
    setLegendHidden(previous => new Set([...previous].filter(key => !seriesKeys.includes(key))));
  };

  useEffect(() => {
    if (chartBackground !== "none" &&
        !comparisonGameObjects.some((game) => game.seriesKey === chartBackground)) {
      setChartBackground("none");
    }
  }, [comparisonGameObjects, chartBackground]);

  const chartBackgroundGame = comparisonGameObjects.find((game) => game.seriesKey === chartBackground) || null;
  const chartBackgroundUrl = chartBackground === "none" ? "" : chartBackgroundGame?.imageUrl || "";

const comparisonHistoryKey = useMemo(
  () =>
    comparisonGameObjects
      .map((g) => `${g.config_id}:${tikTokHistoryKey(g, growthWindow)}`)
      .join("|"),
  [comparisonGameObjects, growthWindow]
);

useEffect(() => {
  if (!showInsights || !comparisonGameObjects.length) return;

  let cancelled = false;

  const pendingGames = comparisonGameObjects
    .filter((game) => game.config_id && game.history_key)
    .map((game) => ({
      game,
      key: tikTokHistoryKey(game, growthWindow),
      slot: `${game.config_id}:${growthWindow === "90d" ? "daily" : "hourly"}`,
    }))
    .filter(({ key, slot }) => !(historyCache[slot] && historyCache[slot].key === key));

  if (!pendingGames.length) return () => { cancelled = true; };

  const loadingUpdates = pendingGames.reduce((updates, { game }) => {
    updates[game.config_id] = true;
    return updates;
  }, {});
  const errorUpdates = pendingGames.reduce((updates, { game }) => {
    updates[game.config_id] = null;
    return updates;
  }, {});
  setHistoryLoading((previous) => ({ ...previous, ...loadingUpdates }));
  setHistoryErrors((previous) => ({ ...previous, ...errorUpdates }));

  const loadSelectedHistories = async () => {
    const results = await Promise.all(pendingGames.map(async ({ game, key, slot }) => {
      try {
        const payload = await loadTikTokHistory(game, growthWindow);
        const rows = normalizeHistoryPayload(payload);
        console.log(
          "TikTok history loaded:",
          game.name,
          "points:",
          rows.length,
          "latest:",
          rows[rows.length - 1]?.ts
        );
        return { game, key, slot, rows, error: null };
      } catch (error) {
        console.error(`❌ Failed to load game history for ${game.name}:`, error);
        return {
          game,
          key,
          slot,
          rows: null,
          error: error?.message || String(error),
        };
      }
    }));

    if (cancelled) return;

    const cacheUpdates = {};
    const completedLoadingUpdates = {};
    const completedErrorUpdates = {};
    results.forEach(({ game, key, slot, rows, error }) => {
      completedLoadingUpdates[game.config_id] = false;
      completedErrorUpdates[game.config_id] = error;
      if (rows) cacheUpdates[slot] = { rows, key, loadedAt: Date.now() };
    });

    if (Object.keys(cacheUpdates).length) {
      setHistoryCache((previous) => ({ ...previous, ...cacheUpdates }));
    }
    setHistoryLoading((previous) => ({ ...previous, ...completedLoadingUpdates }));
    setHistoryErrors((previous) => ({ ...previous, ...completedErrorUpdates }));
  };

  loadSelectedHistories();
  return () => { cancelled = true; };
}, [showInsights, comparisonHistoryKey, tiktokSummaryFreshnessKey, historyRetry]);

  const handleRemove = (name) => setHiddenGames((prev) => [...prev, name]);

  const handleReset = ({ preserveView = false } = {}) => {
    setSearchTerm("");
    setSidebarCollapsed(false);
    setHiddenGames([]);
    setSelectedTags([]);
    setSelectedPublishers([]);
    setSelectedGenres([]);
    setSelectedDevelopers([]);
    setSelectedLiveService(null);
    setSelectedPricing(null);
    setSelectedUGC(null);
    setSelectedFocus([]);
    setSelectedPlatforms([]);
    setSelectedDateRange([null, null]);
    setSortMode("views");
    setRankingWindow("latest");
    setGrowthWindow("30d");
    setOnlyUnreleased(false);
    setShowInsights(false);
    setUseLogScale(false);
    setChartMode("change");
    setChartStyle("area");
    setChartBackground("none");
    setShowAverage(false);
    setShowMedian(false);
    setShow24HourMovingAverage(false);
    setLegendOpen(true);
    setChartColors({});
    setChartHeight(420);
    setHighlighted(new Set());
    setLegendHidden(new Set());
    setComparisonGames([]);
    if (!preserveView) {
      setViewMode("cards");
      setShowTableView(false);
    }
    setMarketSplitView(false);
    setMarketShape("bubbles");
    setMarketSort("views");
    setMarketFavorites([]);
    if (experiment && !preserveView) experiment.chooseView("cards");
  };

  const handleHeaderViewChange = (mode) => {
    setViewMode(mode);
    setShowTableView(mode === "table");
    if (mode !== "market") setMarketSplitView(false);
    if (mode === "market") setFiltersOpen(false);
    if (experiment) experiment.chooseView(mode);
  };

  const applySavedFilters = (filters, { initial = false } = {}) => {
    setSearchTerm(filters.searchTerm ?? "");
    setSelectedTags(filters.tags ?? []);
    setSelectedPublishers(filters.publishers ?? []);
    setSelectedGenres(filters.genres ?? []);
    setSelectedDevelopers(filters.developers ?? []);
    setSelectedLiveService(filters.liveService ?? null);
    setSelectedPricing(filters.pricing ?? null);
    setSelectedUGC(filters.ugc ?? null);
    setSelectedFocus(filters.focus ?? []);
    setSelectedPlatforms(filters.platforms ?? []);
    setSortMode(filters.sortMode ?? "views");
    setRankingWindow(["latest", "1d", "7d"].includes(filters.rankingWindow) ? filters.rankingWindow : "latest");
    setOnlyUnreleased(filters.onlyUnreleased ?? false);
    setGrowthWindow(filters.growthWindow === "180d" ? "90d" :
      ["7d", "30d", "90d"].includes(filters.growthWindow) ? filters.growthWindow : "30d");
    setShowInsights(filters.showInsights ?? false);
    const savedMode = filters.viewMode ?? (filters.showTableView ? "table" : "cards");
    const appliedMode = initial && experiment?.requestedView ? experiment.requestedView : savedMode;
    const tableMode = appliedMode === "table";
    setShowTableView(tableMode);
    setViewMode(appliedMode);
    if (experiment && !initial) experiment.chooseView(appliedMode);
    setMarketSplitView(filters.marketSplitView ?? false);
    setMarketShape(filters.marketShape ?? "bubbles");
    setMarketSort(filters.marketSort ?? "views");
    setMarketFavorites(filters.marketFavorites ?? []);
    setUseLogScale(filters.useLogScale ?? false);
    setChartMode(filters.chartMode ?? "change");
    setChartHeight(filters.chartHeight ?? 420);
    setComparisonGames(filters.comparisonGames ?? []);
    setHiddenGames(filters.hiddenGames ?? []);
    const dateRange = Array.isArray(filters.dateRange) ? [...filters.dateRange] : [null, null];
    if (typeof dateRange[0] === "string") dateRange[0] = new Date(dateRange[0]);
    if (typeof dateRange[1] === "string") dateRange[1] = new Date(dateRange[1]);
    setSelectedDateRange(dateRange);
  };

  const clearSavedFilters = ({ initial = false } = {}) => {
    handleReset({ preserveView: Boolean(initial && experiment) });
    setShowInsights(false);
    setUseLogScale(false);
    setChartMode("change");
    setChartHeight(420);
    setHighlighted(new Set());
    setLegendHidden(new Set());
  };

  useEffect(() => {
    const rowHeight = 56;
    const extraHeight = 200;
    const rows = Math.ceil((layoutViewportHeight() + extraHeight) / rowHeight);
    setSkeletonRowCount(rows);
  }, []);

  const filtered = useMemo(() => {
    let result = games
      .filter((g) => !hiddenGames.includes(g.selectionKey) && !hiddenGames.includes(g.name))
      .filter((g) => !searchTerm.trim() || g.displayName.toLowerCase().includes(searchTerm.trim().toLowerCase()))
      .filter(
        (g) =>
          !selectedTags.length ||
          selectedTags.some((tag) => g.tags.includes(tag.value))
      )
      .filter(
        (g) =>
          !selectedPublishers.length ||
          selectedPublishers.some((pub) => g.publisher === pub.value)
      )
      .filter(
        (g) =>
          !selectedGenres.length ||
          selectedGenres.some((genre) => g.genres?.includes(genre.value))
      )
      .filter(
        (g) =>
          !selectedDevelopers.length ||
          selectedDevelopers.some((dev) => g.developer === dev.value)
      )
      .filter(
        (g) =>
          !selectedLiveService ||
          g.live_service_static === selectedLiveService.value
      )
      .filter((g) => !selectedPricing || g.pricing === selectedPricing.value)
      .filter(
        (g) => !selectedUGC || g.user_generated_content === selectedUGC.value
      )
      .filter((g) => {
        if (!selectedFocus.length) return true;
        const focusVals = String(g.single_multiplayer_focus || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        return selectedFocus.some((f) => focusVals.includes(f.value));
      })
      .filter((g) => {
        if (!selectedPlatforms.length) return true;
        const plats = Array.isArray(g.platforms)
          ? g.platforms
          : String(g.platforms || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
        return selectedPlatforms.some((p) => plats.includes(p.value));
      })
      .filter((g) => {
        if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return true;
        if (!g.release_date) return onlyUnreleased ? true : false;
        const rd = ymdToLocalDate(g.release_date);
        if (!rd) return onlyUnreleased ? true : false;
        const releaseLocal = rd.getTime();
        const minTime = selectedDateRange[0].getTime();
        const maxTime = selectedDateRange[1].getTime();
        return releaseLocal >= minTime && releaseLocal <= maxTime;
      });

    if (onlyUnreleased) result = result.filter((g) => g.isUnreleased);

    return rankTikTokRows(result, rankingWindow, sortMode);
  }, [
    games,
    searchTerm,
    selectedTags,
    selectedPublishers,
    selectedGenres,
    selectedDevelopers,
    selectedLiveService,
    selectedPricing,
    selectedUGC,
    selectedFocus,
    selectedPlatforms,
    selectedDateRange,
    sortMode,
    onlyUnreleased,
    hiddenGames,
    rankingWindow,
  ]);

  const displayed = useMemo(() => filtered, [filtered]);
  const marketBaseGames = useMemo(() => rankTikTokRows(games.filter(game =>
    !hiddenGames.includes(game.selectionKey) && !hiddenGames.includes(game.name)),
  rankingWindow, sortMode === "momentum" ? "views" : sortMode),
  [games, hiddenGames, rankingWindow, sortMode]);
  const hasActiveFilters = Boolean(
    searchTerm.trim() || selectedTags.length || selectedPublishers.length || selectedGenres.length || selectedDevelopers.length ||
    selectedLiveService || selectedPricing || selectedUGC || selectedFocus.length || selectedPlatforms.length || hiddenGames.length ||
    selectedDateRange?.[0] || selectedDateRange?.[1] || onlyUnreleased
  );
  const comparisonSeries = useMemo(() => {
  return comparisonGameObjects.map((game) => ({
    ...game,
    history: historyCache[`${game.config_id}:${growthWindow === "90d" ? "daily" : "hourly"}`]?.rows || [],
  }));
}, [comparisonGameObjects, historyCache, growthWindow]);

const comparisonSeriesWithLoading = useMemo(() => comparisonSeries.map((game) => ({
  ...game,
  isHistoryLoading: !!historyLoading[game.config_id],
})), [comparisonSeries, historyLoading]);

  const lineChartData = useMemo(() => {
    if (!comparisonSeries.length) return [];

    const now = Date.now();
    const windowMs =
      growthWindow === "90d"
        ? 90 * DAY_MS
        : growthWindow === "30d"
        ? 30 * DAY_MS
        : growthWindow === "7d"
        ? 7 * DAY_MS
        : 1 * DAY_MS;

    const startMs = growthWindow === "90d"
      ? Date.parse(new Date(now).toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" })) - 89 * DAY_MS
      : now - windowMs;
    const endMs = now;
    const tsMap = {};

    for (const game of comparisonSeries) {
      const rows = buildRenderableHistory(
        game.history || [],
        chartMode
      ).filter((row) => row.timeMs >= startMs && row.timeMs <= endMs);

      const occurrences = new Map();
      for (const row of rows) {
        if (row.chartValue == null && !row.isDaily) continue;

        const occurrence = occurrences.get(row.timeMs) || 0;
        occurrences.set(row.timeMs, occurrence + 1);
        const key = `${row.timeMs}:${occurrence}`;
        if (!tsMap[key]) {
          tsMap[key] = {
            ts: row.ts,
            timeMs: row.timeMs,
            occurrence,
            partialSeries: {},
          };
        }

        // For Period Cumulative, keep raw deltas here. The visible-window
        // cumulative total is recalculated after the slider window is applied.
        tsMap[key][game.seriesKey] = row.chartValue == null ? null : Number(row.chartValue);
        tsMap[key].partialSeries[game.seriesKey] = row.isPartial;
      }
    }

    return Object.values(tsMap).sort((a, b) => a.timeMs - b.timeMs || a.occurrence - b.occurrence);
  }, [comparisonSeries, chartMode, growthWindow]);

  useEffect(() => {
    const len = lineChartData.length;
    const previousLength = ttPreviousDataLength.current;
    ttPreviousDataLength.current = len;

    if (!len) {
      ttHasInitWindow.current = false;
      setTtWinStart(0);
      setTtWinEnd(0);
      return;
    }

    if (!ttHasInitWindow.current) {
      setTtWinStart(0);
      setTtWinEnd(len - 1);
      ttHasInitWindow.current = true;
    } else {
      setTtWinStart((prev) =>
        Math.max(0, Math.min(prev, len - 1))
      );
      setTtWinEnd((prev) =>
        prev >= previousLength - 1 ? len - 1 : Math.max(0, Math.min(prev, len - 1))
      );
    }
  }, [lineChartData.length, chartMode, growthWindow, comparisonHistoryKey]);

  const labelForTikTokIdx = (idx) => {
    const len = lineChartData.length;
    if (!len) return "";
    const safeIdx = Math.min(Math.max(idx, 0), len - 1);
    return growthWindow === "90d" ? fmtDailyDate(lineChartData[safeIdx]?.ts) : fmtAxisDateTime(lineChartData[safeIdx]?.ts);
  };

  const tikTokRenderData = useMemo(() => {
    const len = lineChartData.length;
    if (!len) return [];

    const s = Math.max(0, Math.min(ttWinStart, len - 1));
    const e = Math.max(s, Math.min(ttWinEnd, len - 1));
    const windowRows = lineChartData.slice(s, e + 1);
    const withMovingAverage = (rows) => {
      if (!show24HourMovingAverage || growthWindow === "90d" || !rows.length) return rows;
      const windowMs = 24 * 60 * 60 * 1000;
      let windowStart = 0;
      return rows.map((row, index) => {
        while (windowStart < index && row.timeMs - rows[windowStart].timeMs >= windowMs) {
          windowStart += 1;
        }
        const hasFullWindow = Number.isFinite(rows[0].timeMs) &&
          Number.isFinite(row.timeMs) && row.timeMs - rows[0].timeMs >= windowMs;
        const output = { ...row };
        comparisonGameObjects.forEach((game) => {
          const averageKey = `${game.seriesKey}__movingAverage24h`;
          if (!hasFullWindow) {
            output[averageKey] = null;
            return;
          }
          let total = 0;
          let count = 0;
          for (let cursor = windowStart; cursor <= index; cursor += 1) {
            const value = rows[cursor][game.seriesKey];
            if (value == null || !Number.isFinite(Number(value))) continue;
            total += Number(value);
            count += 1;
          }
          output[averageKey] = count ? total / count : null;
        });
        return output;
      });
    };

    if (chartMode !== "period") return withMovingAverage(windowRows);

    const acc = Object.create(null);

    const accumulatedRows = windowRows.map((row) => {
      const out = { ...row };

      comparisonGameObjects.forEach((game) => {
        const key = game.seriesKey;
        if (row[key] == null) {
          out[key] = null;
          return;
        }
        const v = Number(row[key]);
        if (!acc[key]) acc[key] = 0;
        if (Number.isFinite(v)) acc[key] += v;
        out[key] = acc[key];
      });

      return out;
    });
    return withMovingAverage(accumulatedRows);
  }, [lineChartData, ttWinStart, ttWinEnd, chartMode, comparisonGameObjects, show24HourMovingAverage, growthWindow]);

  const barChartData = useMemo(() => {
    if (growthWindow !== "1d" || !comparisonSeries.length) {
      return { data: [], items: [], latestTS: null };
    }

    const now = Date.now();
    const startMs = now - DAY_MS;
    const endMs = now;

    const items = comparisonSeries
      .map((game, idx) => {
        const rows = buildRenderableHistory(
          game.history || [],
          chartMode
        ).filter((row) => row.timeMs >= startMs && row.timeMs <= endMs);

        if (!rows.length) return null;

        const latest = rows[rows.length - 1];
        if (!latest || latest.chartValue == null) return null;

        return {
          name: game.displayName || game.name,
          seriesKey: game.seriesKey,
          value: Number(latest.chartValue),
          color: getSeriesColor(game, idx),
          latestTS: latest.ts,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value)
      .slice(0, comparisonGames.length ? undefined : 12);

    const latestTS =
      items.length > 0
        ? items
            .map((it) => new Date(it.latestTS).getTime())
            .filter((v) => !Number.isNaN(v))
            .sort((a, b) => b - a)[0]
        : null;

    const dataRow = items.reduce((acc, it) => {
      acc[it.seriesKey] = it.value;
      return acc;
    }, {});

    return {
      data: items.length ? [dataRow] : [],
      items,
      latestTS: latestTS ? new Date(latestTS).toISOString() : null,
    };
  }, [comparisonSeries, comparisonGames.length, growthWindow, chartMode]);

  const yMax = useMemo(() => {
    const seriesKeys = comparisonGameObjects.map((g) => g.seriesKey);
    let max = 0;

    for (const row of tikTokRenderData) {
      for (const key of seriesKeys) {
        const v = row[key];
        if (typeof v === "number" && v > max) max = v;
      }
    }

    return max;
  }, [tikTokRenderData, comparisonGameObjects]);

  const avgLine = useMemo(() => {
  if (
    chartMode !== "change" ||
    comparisonGames.length === 0
  ) {
    return null;
  }

  const activeSeries = comparisonGameObjects.filter(
    (game) => !legendHidden.has(game.seriesKey)
  );

  const allValues = tikTokRenderData.flatMap((row) =>
    activeSeries
      .map((game) => row[game.seriesKey])
      .filter((v) => typeof v === "number")
  );

  return allValues.length
    ? allValues.reduce((a, b) => a + b, 0) / allValues.length
    : null;
}, [tikTokRenderData, comparisonGameObjects, comparisonGames, legendHidden, growthWindow, chartMode]);

const avgSeriesKey = "__Average__";
const medianSeriesKey = "__Median__";

const medianLine = useMemo(() => {
  if (chartMode !== "change") return null;
  const activeSeries = comparisonGameObjects.filter((game) => !legendHidden.has(game.seriesKey));
  return median(tikTokRenderData.flatMap((row) => activeSeries.map((game) => row[game.seriesKey]).filter((value) => Number.isFinite(value))));
}, [chartMode, tikTokRenderData, comparisonGameObjects, legendHidden]);

const avgSeriesData = useMemo(() => {
  if (
    chartMode === "change" ||
    comparisonGames.length < 2 ||
    !tikTokRenderData.length
  ) {
    return tikTokRenderData;
  }

  const activeSeries = comparisonGameObjects.filter(
    (game) => !legendHidden.has(game.seriesKey)
  );
  if (activeSeries.length < 2) return tikTokRenderData;

  const lastSeen = {};
  return tikTokRenderData.map((row) => {
    activeSeries.forEach((game) => {
      if (Number.isFinite(row[game.seriesKey])) lastSeen[game.seriesKey] = row[game.seriesKey];
    });

    const values = activeSeries
      .map((game) => lastSeen[game.seriesKey])
      .filter((v) => typeof v === "number" && Number.isFinite(v));

    return {
      ...row,
      [avgSeriesKey]: values.length >= 2
        ? values.reduce((a, b) => a + b, 0) / values.length
        : null,
      [medianSeriesKey]: values.length >= 2 ? median(values) : null,
    };
  });
}, [tikTokRenderData, comparisonGameObjects, comparisonGames, legendHidden, growthWindow, chartMode]);

const chartRenderData = avgSeriesData;
const logScaleBlocked = useLogScale && chartRenderData.some(row =>
  comparisonGameObjects.some(game => row[game.seriesKey] < 0));

  const labelPoints = useMemo(() => {
  const chartRows =
    chartMode === "change" ? tikTokRenderData : avgSeriesData;

  return comparisonGameObjects
    .filter((game) => !legendHidden.has(game.seriesKey))
    .map((game) => {
      let max = -Infinity;
      let maxRow = null;

      for (const row of chartRows) {
        const v = row[game.seriesKey];
        if (typeof v === "number" && Number.isFinite(v) && v > max) {
          max = v;
          maxRow = row;
        }
      }

      if (!maxRow || !Number.isFinite(max)) return null;

      const originalIdx = comparisonGameObjects.findIndex(
        (g) => g.seriesKey === game.seriesKey
      );
      const color =
        originalIdx >= 0 ? getSeriesColor(game, originalIdx) : TOTAL_LINE_COLOR;
      const dim = highlighted.size > 0 && !highlighted.has(game.seriesKey);

      return {
        name: game.displayName || game.name,
        seriesKey: game.seriesKey,
        x: maxRow.ts,
        y: max,
        color,
        dim,
        key: `${game.seriesKey}-${maxRow.ts}`,
      };
    })
    .filter(Boolean);
}, [
  tikTokRenderData,
  avgSeriesData,
  comparisonGameObjects,
  growthWindow,
  legendHidden,
  highlighted,
  chartMode,
  chartColors,
]);
  const totalViews = useMemo(
    () => displayed.reduce((sum, g) => sum + (g[sortMode === "videos" ? "videosTotal" : "viewsTotal"] || 0), 0),
    [displayed, sortMode]
  );

  const [gridLeftPad, setGridLeftPad] = useState(0);
  const gridHostRef = useRef(null);

  useEffect(() => {
    const computeLeft = () => {
      const host = gridHostRef.current;
      if (!host) return;
      const w = host.clientWidth || 0;
      if (w <= 0) return;
      const cols = Math.max(
        1,
        Math.floor((w + GRID_COL_GAP) / (CARD_WIDTH + GRID_COL_GAP))
      );
      const gridW = cols * CARD_WIDTH + Math.max(0, cols - 1) * GRID_COL_GAP;
      const offset = Math.max(0, Math.floor((w - gridW) / 2));
      setGridLeftPad(offset);
    };

    computeLeft();
    window.addEventListener("resize", computeLeft);
    return () => window.removeEventListener("resize", computeLeft);
  }, [choosingView]);

  const selectLikeBtn = {
    width: "100%",
    height: 36,
    minHeight: 36,
    background: "#050505",
    color: "rgba(187,187,187,0.75)",
    border: "1px solid #444",
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 400,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.3,
    fontFamily: "inherit",
  };

const chartControlBtn = (active) => ({
  height: 28,
  padding: "0 10px",
  fontSize: 12,
  fontWeight: 800,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.16)",
  background: active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.03)",
  color: active ? "#fff" : "#cfd8e3",
  cursor: "pointer",
  boxShadow: active ? "0 0 12px rgba(180,190,200,0.16)" : "none",
});

const ChartGameLabels = () => {
  const orderedGames = comparisonGameObjects;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 10020,
        pointerEvents: "auto",
        isolation: "isolate",
        display: "flex",
        flexWrap: "wrap",
        gap: 7,
        justifyContent: "flex-start",
        alignItems: "center",
        padding: "10px 18px 6px 108px",
      }}
    >
      <button
        type="button"
        onClick={() => setLegendOpen((value) => !value)}
        aria-label={legendOpen ? "Hide legend" : "Show legend"}
        title={legendOpen ? "Hide legend" : "Show legend"}
        style={{ height: 26, minWidth: legendOpen ? 26 : 104, padding: legendOpen ? 0 : "0 8px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.03)", color: "rgba(238,243,247,0.88)", fontSize: 11, fontWeight: 750, cursor: "pointer", flexShrink: 0 }}
      >
        {legendOpen ? "−" : "+ Show Legend"}
      </button>
      {legendOpen && orderedGames.map((game, idx) => {
        const color = getSeriesColor(game, idx);
        const seriesKey = game.seriesKey;
        const selected = highlighted.size === 0 || highlighted.has(seriesKey);
        const dim = !selected;

        return (
          <span
            key={seriesKey}
            onClick={(event) => highlightSeries(seriesKey, event)}
            title="Click to highlight; Ctrl/Cmd-click to multi-select"
            style={{ position: "relative", zIndex: 10021, cursor: "pointer", color: "#fff", fontWeight: 750, fontSize: 11, lineHeight: "15px", display: "inline-flex", alignItems: "center", gap: 6, opacity: dim ? 0.62 : 1, background: "transparent", padding: "3px 4px", borderRadius: 7, userSelect: "none", textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}
          >
            <input
              type="color"
              value={color}
              onChange={(event) => {
                const nextColor = event.currentTarget.value;
                setChartColors((previous) => ({ ...previous, [seriesKey]: nextColor }));
              }}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              title={`Change ${game.displayName || game.name} color`}
              aria-label={`Change ${game.displayName || game.name} color`}
              style={{ position: "relative", zIndex: 10022, width: 12, height: 12, padding: 0, border: "2px solid #050505", borderRadius: 4, background: color, cursor: "pointer", flexShrink: 0, boxShadow: "0 0 0 1px rgba(255,255,255,0.28), 0 2px 7px rgba(0,0,0,0.65)" }}
            />
            <span style={{ maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={game.displayName || game.name}>
              {game.displayName || game.name}
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeComparisonGame(game);
              }}
              aria-label={`Remove ${game.displayName || game.name} from chart`}
              title="Remove series"
              style={{ width: 16, height: 16, border: "none", background: "transparent", color: "#fff", opacity: 0.72, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1, fontSize: 13 }}
            >×</button>
          </span>
        );
      })}
    </div>
  );
};

  const chartMargins = { left: 8, right: 80, top: 20, bottom: 0 };

  const exportCSV = () => {
    if (!filtered.length) return;
    const columns = [
      ["Rank", (_, index) => index + 1], ["Game", (game) => game.name],
      ["Config_ID", (game) => game.config_id ?? ""], ["Videos", (game) => game.videosTotal ?? ""],
      ["Views", (game) => game.viewsTotal ?? ""], ["Ranking_Window", () => rankingWindow],
      ["Ranking_Metric", () => sortMode], ["Latest_Views", game => game.views], ["Latest_Videos", game => game.videos],
      ["Views_Growth_1d", game => game.window_1d?.available ? game.window_1d.views : ""],
      ["Views_Growth_7d", game => game.window_7d?.available ? game.window_7d.views : ""],
      ["7d_Coverage", game => game.coverageLabel || ""],
      ["Hashtag", (game) => game.hashtag], ["Publisher", (game) => game.publisher || ""],
      ["Developer", (game) => game.developer || ""], ["Genres", (game) => (game.genres || []).join(" | ")],
      ["Platforms", (game) => (game.platforms || []).join(" | ")], ["Release_Date", (game) => game.release_date || ""],
    ];
    const escapeCSV = (value) => {
      const text = String(value ?? "");
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [columns.map(([name]) => name).join(","), ...filtered.map((game, index) => columns.map(([, value]) => escapeCSV(value(game, index))).join(","))].join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mindGAME - TikTok Visible Games.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const BarLegendContent = ({ payload }) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        paddingTop: 6,
        justifyContent: "center",
      }}
    >
      {payload?.map((item, i) => {
        const name = item.value;
        const color = item.color || item.payload?.stroke || item.payload?.fill;
        return (
          <span
            key={i}
            style={{
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              opacity: 0.9,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: color || "#888",
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            {name}
          </span>
        );
      })}
    </div>
  );

  const LineLegendContent = () => {
    const series = comparisonGameObjects;

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          paddingTop: 6,
          justifyContent: "center",
        }}
      >
        {series.map((game, i) => {
          const color = seriesColor(i);
          const seriesKey = game.seriesKey;
          const label = game.displayName || game.name;
          const isHi = highlighted.size > 0 ? highlighted.has(seriesKey) : true;
          const dim = highlighted.size > 0 && !isHi;
          const hidden = legendHidden.has(seriesKey);

          return (
            <span
              key={seriesKey}
              onClick={(event) => highlightSeries(seriesKey, event)}
              style={{
                cursor: "pointer",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                opacity: hidden ? 0.3 : dim ? 0.5 : 1,
                padding: "2px 6px",
                borderRadius: 6,
              }}
              title={label}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  background: color,
                  display: "inline-block",
                  borderRadius: 2,
                }}
              />
              {label}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLegendHidden((prev) => {
                    const s = new Set(prev);
                    if (s.has(seriesKey)) s.delete(seriesKey);
                    else s.add(seriesKey);
                    if (s.has(seriesKey)) {
                      setHighlighted((h) => {
                        const h2 = new Set(h);
                        h2.delete(seriesKey);
                        return h2;
                      });
                    }
                    return s;
                  });
                }}
                style={{
                  marginLeft: 6,
                  width: 16,
                  height: 16,
                  lineHeight: "14px",
                  borderRadius: 4,
                  border: "1px solid #444",
                  background: "#111",
                  color: "#ccc",
                  fontWeight: 900,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {hidden ? "↺" : "×"}
              </button>
            </span>
          );
        })}
      </div>
    );
  };

  const manifestGeneratedAt = tikTokManifest?.generated_at
    ? new Date(tikTokManifest.generated_at).toLocaleString()
    : null;
  const publicationStale = tikTokManifest && (
    Date.now() - Date.parse(tikTokManifest.generated_at) > 90 * 60 * 1000 ||
    Date.now() - Date.parse(tikTokManifest.latest_source_dt) > 36 * HOUR_MS
  );

  const seriesIntro = useSeriesIntroAnimation(comparisonGameObjects.map(game => game.seriesKey));
  const hasLoadedComparisonData = comparisonSeries.some(
    (g) => Array.isArray(g.history) && g.history.length
  );

  const chartBusy =
    showInsights &&
    comparisonSeriesWithLoading.some((g) => g.isHistoryLoading) &&
    !hasLoadedComparisonData;
  const initialDashboardLoading =
    (globalLoading || loading) && games.length === 0 && !datasetError && !preparationError;

  return (
    <div
      className="mg-jura-dashboard mg-tiktok-page"
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: !choosingView && viewMode === "market" ? "calc(calc(var(--mg-vh) * 100) - 78px)" : "calc(var(--mg-vh) * 100)",
        height: !choosingView && viewMode === "market" ? "calc(calc(var(--mg-vh) * 100) - 78px)" : undefined,
        flexDirection: experiment ? "column" : undefined,
      }}
    >
      <MindshareHeaderControls
        visible
        targetId="tiktok-header-controls"
        savedControlsId="tiktok-header-saved-controls"
        centerSearchTargetId="tiktok-header-search"
        ariaLabel="TikTok views and filters"
        views={TIKTOK_HEADER_VIEWS}
        searchAriaLabel="Search TikTok games"
        viewMode={viewMode}
        onViewChange={handleHeaderViewChange}
        onOpenFilters={() => setFiltersOpen((open) => !open)}
        filtersOpen={filtersOpen}
        filtersActive={hasActiveFilters}
        toolsExpanded={hasActiveFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
      {headerToolsTarget && createPortal(
        <>
          <SavedViewsManager
            compact
            allowLocalFallback
            type="tiktok"
            currentFilters={{
              searchTerm, tags: selectedTags, publishers: selectedPublishers, genres: selectedGenres,
              developers: selectedDevelopers, liveService: selectedLiveService, pricing: selectedPricing,
              ugc: selectedUGC, focus: selectedFocus, platforms: selectedPlatforms, dateRange: selectedDateRange,
              sortMode, rankingWindow, onlyUnreleased, growthWindow, showInsights, showTableView, viewMode, marketSplitView,
              marketShape, marketSort, marketFavorites,
              useLogScale, chartMode, chartHeight, comparisonGames, hiddenGames,
            }}
            onApplyFilters={applySavedFilters}
            onClearFilters={clearSavedFilters}
          />
          <button type="button" className="ms-header-icon" onClick={handleReset} aria-label="Reset all" data-tooltip="Reset all"><RotateCcw size={16} /></button>
          <CsvExportButton className="ms-header-icon" onClick={exportCSV} disabled={!filtered.length} aria-label="Export CSV" data-tooltip="Export CSV"><Download size={16} /></CsvExportButton>
        </>,
        headerToolsTarget
      )}
      {filtersOpen && (
        <div
          className="mg-steam-filter-backdrop"
          aria-hidden="true"
          onClick={() => setFiltersOpen(false)}
        />
      )}

      <div
        className="mg-twitch-filter-popover mg-mindshare-filter-popover"
        onMouseEnter={() => setFiltersOpen(true)}
        onMouseLeave={() => setFiltersOpen(false)}
        style={{
          position: "fixed",
          top: 82,
          left: 20,
          width: "min(440px, calc(calc(var(--mg-vw) * 100) - 40px))",
          maxHeight: "calc(calc(var(--mg-vh) * 100) - 102px)",
          minWidth: 0,
          height: "auto",
          padding: "42px 20px 20px",
          background: "rgba(8,10,12,0.97)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 18,
          boxShadow: "0 22px 60px rgba(0,0,0,0.82)",
          zIndex: 10000,
          opacity: filtersOpen ? 1 : 0,
          visibility: filtersOpen ? "visible" : "hidden",
          pointerEvents: filtersOpen ? "auto" : "none",
          transform: filtersOpen ? "translateY(0)" : "translateY(-12px)",
          transition: "opacity 150ms ease, transform 150ms ease, visibility 150ms ease",
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            width: "100%",
            minWidth: 0,
          }}
        >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(187,187,187,0.70)", fontSize: 11, fontWeight: 900, letterSpacing: "0.16em" }}>
          <span style={{ flex: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }} />
          <span>FILTERS</span>
          <span style={{ flex: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }} />
        </div>

        <VirtualizedDropdown
          placeholder="All Tags"
          value={selectedTags}
          onChange={setSelectedTags}
          options={allTags.map((t) => ({ label: t, value: t }))}
          isClearable
          isMulti
        />
        <VirtualizedDropdown
          placeholder="All Genres"
          value={selectedGenres}
          onChange={setSelectedGenres}
          options={allGenres.map((g) => ({ label: g, value: g }))}
          isClearable
          isMulti
        />
        <VirtualizedDropdown
          placeholder="All Publishers"
          value={selectedPublishers}
          onChange={setSelectedPublishers}
          options={allPublishers.map((p) => ({ label: p, value: p }))}
          isClearable
          isMulti
        />
        <VirtualizedDropdown
          placeholder="All Developers"
          value={selectedDevelopers}
          onChange={setSelectedDevelopers}
          options={allDevelopers.map((d) => ({ label: d, value: d }))}
          isClearable
          isMulti
        />
        <VirtualizedDropdown
          placeholder="Business Model"
          value={selectedLiveService}
          onChange={setSelectedLiveService}
          options={allLiveService.map((s) => ({ label: s, value: s }))}
          isClearable
        />
        <VirtualizedDropdown
          placeholder="Pricing"
          value={selectedPricing}
          onChange={setSelectedPricing}
          options={allPricing.map((p) => ({ label: p, value: p }))}
          isClearable
        />
        <VirtualizedDropdown
          placeholder="UGC"
          value={selectedUGC}
          onChange={setSelectedUGC}
          options={allUGC.map((ugc) => ({ label: ugc, value: ugc }))}
          isClearable
        />
        <VirtualizedDropdown
          placeholder="Play Type"
          value={selectedFocus}
          onChange={setSelectedFocus}
          options={allFocus.map((f) => ({ label: f, value: f }))}
          isClearable
          isMulti
        />
        <VirtualizedDropdown
          placeholder="Platform Type"
          value={selectedPlatforms}
          onChange={setSelectedPlatforms}
          options={allPlatforms.map((p) => ({ label: p, value: p }))}
          isClearable
          isMulti
        />

        <DateSlider
          compact
          headerContent={
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "rgba(187,187,187,0.75)", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={onlyUnreleased}
                onChange={(e) => setOnlyUnreleased(e.target.checked)}
              />
              Show Only Unreleased
            </label>
          }
          range={releaseDateRange}
          selected={selectedDateRange}
          onChange={(range) => setSelectedDateRange(range || [null, null])}
          onClear={() => setSelectedDateRange([null, null])}
          forceClose={!filtersOpen}
        />

        </div>
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen(false)}
          style={{
            width: "100%",
            height: 34,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.16)",
            background: "rgba(255,255,255,0.03)",
            color: "rgba(238,243,247,0.88)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          Close Filters
        </button>
      </div>

      {choosingView && <TikTokViewExhibition games={games} loading={globalLoading || loading} error={datasetError || preparationError} onRetry={refreshTikTokData} onChoose={handleHeaderViewChange} />}
      {experiment && <TikTokViewfinderButton open={choosingView} onClick={() => experiment.chooseView(choosingView ? viewMode : "views")} />}
      {!choosingView && <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", padding: viewMode === "market" ? "4px 30px 0" : "4px 30px 30px 30px" }}>
        <div ref={gridHostRef} style={{ flex: 1, minWidth: 0, minHeight: 0, display: viewMode === "market" ? "flex" : undefined, flexDirection: "column" }}>

          {viewMode !== "market" && showInsights && comparisonGames.length > 0 && (
            <div
              style={{
                marginTop: 0,
                marginBottom: 16,
                padding: "12px 18px 18px",
                background: "#000",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {chartBackgroundUrl && (
                <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: `url(${chartBackgroundUrl})`, backgroundSize: "cover", backgroundPosition: "center 24%", opacity: 0.48, filter: "blur(3px) saturate(1.08) brightness(0.58)", transform: "scale(1.06)", pointerEvents: "none" }} />
              )}
              {chartBackgroundUrl && (
                <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, background: "linear-gradient(90deg, rgba(0,0,0,0.72), rgba(0,0,0,0.48) 48%, rgba(0,0,0,0.72)), linear-gradient(180deg, rgba(0,0,0,0.34), rgba(0,0,0,0.62))", pointerEvents: "none" }} />
              )}
              <div
  style={{
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 0,
    gap: 10,
    padding: "0 0 8px",
    borderRadius: 0,
    background: "transparent",
    border: "none",
    boxShadow: "none",
    position: "relative",
    zIndex: 2,
  }}
>

  <div style={{ display: "flex", width: "calc(100% - 108px)", marginLeft: 108, gap: 8, alignItems: "center", justifyContent: "flex-start", flexWrap: "wrap" }}>
  <TikTokChartSelect
    value={growthWindow}
    onChange={setGrowthWindow}
    ariaLabel="TikTok chart period"
    icon={<CalendarDays size={14} strokeWidth={1.9} />}
    width={168}
    options={[
      { value: "7d", label: "Past Week" },
      { value: "30d", label: "Past Month" },
      { value: "90d", label: "Past 90 Days · Daily" },
    ]}
  />

  <span aria-hidden style={{ width: 1, height: 20, background: "rgba(255,255,255,0.22)", margin: "0 2px" }} />

  <TikTokChartSelect
    value={chartMode}
    onChange={setChartMode}
    ariaLabel="TikTok chart metric"
    width={230}
    icon={chartMode === "change" ? <Waves size={14} /> : chartMode === "period" ? <TrendingUp size={14} /> : (
      <span style={{ position: "relative", display: "inline-flex", width: 16, height: 16 }}>
        <TrendingUp size={16} />
        <span style={{ position: "absolute", left: 4, top: 5, display: "flex", background: "#0b0b0b" }}>
          <Slash size={7} /><Slash size={7} style={{ marginLeft: -3 }} />
        </span>
      </span>
    )}
    options={[
      { value: "change", label: "Views Added", description: "Activity gained per hour or day." },
      { value: "period", label: "Period Total", description: "Views gained since the start of the selected window." },
      { value: "lifetime", label: "Lifetime Total", description: "Cumulative hashtag views at each observation." },
    ]}
  />

  <TikTokChartSelect
    value={chartStyle}
    onChange={setChartStyle}
    ariaLabel="Chart style"
    width={168}
    options={[
      { value: "area", label: "Area" },
      { value: "line", label: "Line" },
    ]}
  />

  <TikTokChartTools
    showAverage={showAverage}
    showMedian={showMedian}
    useLogScale={useLogScale}
    show24HourMovingAverage={show24HourMovingAverage}
    isHourlyChart={growthWindow !== "90d"}
    onAverageChange={setShowAverage}
    onMedianChange={setShowMedian}
    onLogChange={setUseLogScale}
    on24HourMovingAverageChange={setShow24HourMovingAverage}
  />

  <TikTokChartSelect
    value={chartBackground}
    onChange={setChartBackground}
    ariaLabel="Chart background"
    icon={<ImageIcon size={14} strokeWidth={1.9} />}
    width={168}
    options={[
      { value: "none", label: "No Background" },
      ...comparisonGameObjects.map((game) => ({ value: game.seriesKey, label: game.displayName || game.name })),
    ]}
  />

  <button
    onClick={() => {
      setComparisonGames([]);
      setHighlighted(new Set());
      setLegendHidden(new Set());
      setChartColors({});
      setChartBackground("none");
      setChartStyle("area");
      setUseLogScale(false);
      setShowAverage(false);
      setShowMedian(false);
      setShowInsights(false);
    }}
    aria-label="Reset games"
    title="Reset games"
    style={{ width: 26, height: 26, padding: 0, borderRadius: 8, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.03)", color: "rgba(238,243,247,0.88)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
  >
    <RotateCcw size={15} strokeWidth={1.9} aria-hidden="true" />
  </button>

</div>
</div>


              {logScaleBlocked && <p role="status" style={{ color: "#f5e6c8", margin: "8px 0", fontSize: 12 }}>
                Using linear scale: net views include negative values, which cannot use a logarithmic scale.
              </p>}
              {chartBusy ? (
                <MindshareChartSkeleton height={chartHeight} message="Loading TikTok history..." />
              ) : (
                <div
  style={{
    height: "auto",
    margin: 0,
    borderRadius: 8,
    overflow: "visible",
    backgroundColor: "transparent",
    position: "relative",
    zIndex: 1,
  }}
>
  <div
    style={{
      height: chartHeight,
      minHeight: 300,
      maxHeight: 780,
      overflow: "hidden",
      position: "relative",
    }}
  >
                  <ResponsiveContainer width="100%" height="100%">
                    {false ? (
                      <BarChart
                        data={barChartData.data}
                        margin={{ left: 66, right: 130, top: 22, bottom: 0 }}
                      >
                        <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey={() => ""} tick={false} axisLine={false} />
                        <YAxis
                          width={100}
                          tickFormatter={(v) => v.toLocaleString()}
                          stroke="#fff"
                        />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload || !payload.length) return null;
                            const rows = payload
                              .map((p) => ({
                                name: p.dataKey,
                                value: p.value,
                                color: p.fill || p.color,
                              }))
                              .filter((r) => typeof r.value === "number")
                              .sort((a, b) => b.value - a.value);

                            if (!rows.length) return null;

                            return (
                              <div
                                style={{
                                  background: "#222",
                                  border: "1px solid #555",
                                  padding: 10,
                                  color: "#fff",
                                  fontSize: 13,
                                }}
                              >
                                <strong>
                                  {fmtTooltipDateTime(barChartData.latestTS || new Date())}
                                </strong>
                                {rows.map((r, i) => (
                                  <div key={i} style={{ color: r.color || "#fff" }}>
                                    {r.name}: {r.value?.toLocaleString()}
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        
                        {barChartData.items.map((it) => (
                          <Bar
                            key={it.seriesKey}
                            dataKey={it.seriesKey}
                            name={it.name}
                            isAnimationActive
                            animationDuration={650}
                            animationEasing="ease-out"
                            radius={[6, 6, 0, 0]}
                          >
                            <Cell fill={it.color} />
                          </Bar>
                        ))}
                      </BarChart>
                    ) : (
                      <ComposedChart data={chartRenderData} margin={chartMargins}>
                        <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                          dataKey="ts"
                          stroke="#ddd"
                          padding={{ left: 0, right: 0 }}
                          tickFormatter={
                            growthWindow === "90d" ? fmtDailyDate : chartMode !== "change" || growthWindow !== "30d"
                              ? fmtAxisDateTime
                              : fmtAxisDate
                          }
                          tick={{
                            fontWeight: 700,
                            fontSize: 11,
                            fontFamily: "inherit",
                            fill: "#cfd8e3",
                          }}
                          tickLine={{ stroke: "rgba(255,255,255,0.18)" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.16)" }}
                          minTickGap={28}
                        />
                        <YAxis
                          width={100}
                          scale={useLogScale && !logScaleBlocked ? "log" : "linear"}
                          domain={
                            useLogScale && !logScaleBlocked
                              ? [
                                  (dataMin) =>
                                    Math.max(
                                      1,
                                      Math.floor(Math.min(dataMin ?? yMax, yMax) * 0.98)
                                    ),
                                  (dataMax) =>
                                    Math.ceil(Math.max(dataMax ?? yMax, yMax) * 1.22),
                                ]
                              : [
                                  (dataMin) =>
                                    Math.min(0, Math.floor((dataMin ?? 0) * 1.05)),
                                  (dataMax) =>
                                    Math.ceil((dataMax ?? yMax) * 1.22),
                                ]
                          }
                          tickFormatter={(v) => v.toLocaleString()}
                          stroke="#ddd"
                          tick={{
                            fontWeight: 400,
                            fontSize: 12,
                            fontFamily: "inherit",
                            fill: "#cfd8e3",
                          }}
                          tickLine={{ stroke: "rgba(255,255,255,0.18)" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.16)" }}
                          label={{                           value: chartMode === "change" ? "Views Added" : chartMode === "period" ? "Period Total" : "Lifetime Total", angle: -90, position: "insideLeft", fill: "#cfd8e3", fontSize: 12, fontWeight: 700, offset: 8 }}
                        />

                        <Tooltip
                          isAnimationActive={false}
                          content={({ label, payload }) => {
                            const entries = [...(payload || [])]
                              .filter((entry) => {
                                if (highlighted.size === 0) return true;
                                const dataKey = String(entry.dataKey || "");
                                const seriesKey = dataKey.endsWith("__movingAverage24h")
                                  ? dataKey.slice(0, -"__movingAverage24h".length)
                                  : dataKey;
                                return highlighted.has(seriesKey);
                              })
                              .sort((a, b) => (b.value || 0) - (a.value || 0));
                            if (!entries.length) return null;

                            return (
                              <div
                                style={{
                                  background: "rgba(7,9,11,0.97)",
                                  border: "1px solid rgba(255,255,255,0.16)",
                                  borderRadius: 10,
                                  padding: 12,
                                  color: "#fff",
                                  fontSize: 13,
                                  boxShadow: "0 16px 38px rgba(0,0,0,0.78)",
                                }}
                              >
                                <strong>{growthWindow === "90d" ? `${fmtDailyDate(label)} · Pacific day` : fmtTooltipDateTime(label)}</strong>
                                {entries.map((entry, i) => (
                                  <div key={i} style={{ color: entry.color }}>
                                    {entry.name === avgSeriesKey ? "Average" : entry.name === medianSeriesKey ? "Median" : entry.name}:{" "}
                                    {entry.value?.toLocaleString()}
                                    {entry.payload?.partialSeries?.[entry.dataKey] && " (partial day)"}
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />


                        {comparisonGameObjects.map((game, idx) => {
                          const dim =
                            highlighted.size > 0 && !highlighted.has(game.seriesKey);
                          const stroke = getSeriesColor(game, idx);

                          if (legendHidden.has(game.seriesKey) ||
                              !chartRenderData.some(row => row[game.seriesKey] != null)) return null;

                          return chartStyle === "area" ? (
                            <Area
                              key={game.seriesKey}
                              type="monotone"
                              dataKey={game.seriesKey}
                              name={game.displayName || game.name}
                              {...seriesIntro(game.seriesKey)}
                              stroke={stroke}
                              strokeWidth={dim ? 1.2 : 2.4}
                              strokeOpacity={dim ? 0.2 : 1}
                              fill={stroke}
                              fillOpacity={dim ? 0.018 : 0.12}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              dot={comparisonSeries.find(series => series.seriesKey === game.seriesKey)?.history.length === 1 ? { r: 3 } : false}
                              connectNulls={growthWindow !== "90d"}
                            />
                          ) : (
                            <Line
                              key={game.seriesKey}
                              type="monotone"
                              dataKey={game.seriesKey}
                              name={game.displayName || game.name}
                              {...seriesIntro(game.seriesKey)}
                              stroke={stroke}
                              strokeWidth={dim ? 1.6 : 2.6}
                              strokeOpacity={dim ? 0.2 : 1}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              dot={comparisonSeries.find(series => series.seriesKey === game.seriesKey)?.history.length === 1 ? { r: 3 } : false}
                              connectNulls={growthWindow !== "90d"}
                            />
                          );
                        })}

                        {show24HourMovingAverage && growthWindow !== "90d" && comparisonGameObjects.map((game, idx) => {
                          const seriesKey = `${game.seriesKey}__movingAverage24h`;
                          const dim = highlighted.size > 0 && !highlighted.has(game.seriesKey);
                          if (legendHidden.has(game.seriesKey) ||
                              !chartRenderData.some(row => row[seriesKey] != null)) return null;
                          return (
                            <Line
                              key={seriesKey}
                              type="monotone"
                              dataKey={seriesKey}
                              name={`${game.displayName || game.name} 24h moving average`}
                              stroke={getSeriesColor(game, idx)}
                              strokeWidth={dim ? 1.4 : 2}
                              strokeOpacity={dim ? 0.25 : 0.95}
                              strokeDasharray="5 4"
                              dot={false}
                              connectNulls={false}
                            />
                          );
                        })}

                        {showAverage && chartMode !== "change" &&
  comparisonGameObjects.filter((game) => !legendHidden.has(game.seriesKey)).length > 1 && (
    <Line
      key={avgSeriesKey}
      type="monotone"
      dataKey={avgSeriesKey}
      name="Average"
      stroke="rgba(230,230,230,0.92)"
      strokeWidth={1.6}
      strokeDasharray="5 5"
      strokeLinecap="round"
      strokeLinejoin="round"
      dot={false}
      connectNulls={false}
      isAnimationActive={false}
      label={false}
    />
)}

{showAverage && chartMode !== "change" &&
  comparisonGameObjects.filter((game) => !legendHidden.has(game.seriesKey)).length > 1 &&
  (() => {
    const lastAvgRow = [...avgSeriesData]
      .reverse()
      .find((r) => typeof r[avgSeriesKey] === "number");

    if (!lastAvgRow) return null;

    return (
      <ReferenceDot
        x={lastAvgRow.ts}
        y={lastAvgRow[avgSeriesKey]}
        r={0}
        isFront
        label={{
          value: `Avg: ${fmtAbbrev(lastAvgRow[avgSeriesKey])}`,
          fill: "#ccc",
          position: "right",
          fontSize: 12,
          fontWeight: 400,
        }}
      />
    );
  })()}

                        {showMedian && chartMode !== "change" && comparisonGameObjects.some((game) => !legendHidden.has(game.seriesKey)) && (
                          <Line dataKey={medianSeriesKey} name="Median" type="monotone" stroke="rgba(245,230,200,0.90)" strokeWidth={1.6} strokeDasharray="3 5" dot={false} connectNulls={false} isAnimationActive={false} />
                        )}
                        {showMedian && Number.isFinite(medianLine) && (
                          <ReferenceLine y={medianLine} stroke="rgba(245,230,200,0.90)" strokeDasharray="3 5" label={{ value: `Median: ${fmtAbbrev(medianLine)}`, fill: "#F5E6C8", position: "right", fontSize: 12, fontWeight: 400 }} />
                        )}
                        {showAverage && avgLine && (
                          <ReferenceLine
                            y={avgLine}
                            stroke="rgba(230,230,230,0.92)"
                            strokeDasharray="5 5"
                            label={{
                              value: `Avg: ${fmtAbbrev(avgLine)}`,
                              fill: "#ccc",
                              position: "right",
                              fontSize: 12,
                              fontWeight: 400,
                            }}
                          />
                        )}

                        {labelPoints.map((p) => (
                          <ReferenceDot
                            key={p.key}
                            x={p.x}
                            y={p.y}
                            r={3}
                            isFront
                            fill={p.color}
                            stroke="none"
                            fillOpacity={p.dim ? 0.2 : 1}
                            label={{
                              value: p.name,
                              position: "top",
                              fill: p.color,
                              fontSize: 13,
                              fontWeight: 700,
                              opacity: p.dim ? 0.2 : 1,
                              role: "button",
                              tabIndex: 0,
                              "aria-label": `Highlight ${p.name}`,
                              "aria-pressed": highlighted.has(p.seriesKey),
                              style: { cursor: "pointer", pointerEvents: "auto", userSelect: "none", WebkitUserSelect: "none" },
                              onMouseDown: event => event.preventDefault(),
                              onClick: (event) => {
                                highlightSeries(p.seriesKey, event);
                                if (event.detail > 0) event.currentTarget.blur();
                              },
                              onKeyDown: (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  highlightSeries(p.seriesKey, event);
                                }
                              },
                            }}
                          />
                        ))}
                      </ComposedChart>
                    )}
                  </ResponsiveContainer>
                  </div>

                  {lineChartData.length > 1 && (
                    <div style={{ marginTop: 8, marginLeft: 108, marginRight: 80, flex: "0 0 auto" }}>
                      <style>{`
                        .mg-tiktok-range {
                          -webkit-appearance: none;
                          appearance: none;
                        }
                        .mg-tiktok-range::-webkit-slider-runnable-track {
                          height: 18px;
                          background: transparent;
                          border: none;
                        }
                        .mg-tiktok-range::-moz-range-track {
                          height: 18px;
                          background: transparent;
                          border: none;
                        }
                        .mg-tiktok-range::-webkit-slider-thumb {
                          -webkit-appearance: none;
                          appearance: none;
                          width: 28px;
                          height: 14px;
                          border-radius: 7px;
                          border: none;
                          background: #9aa3ad;
                          cursor: grab;
                          box-shadow: 0 0 10px rgba(180,190,200,0.28);
                          pointer-events: auto;
                          margin-top: 2px;
                        }
                        .mg-tiktok-range::-moz-range-thumb {
                          width: 28px;
                          height: 14px;
                          border-radius: 7px;
                          border: none;
                          background: #9aa3ad;
                          cursor: grab;
                          box-shadow: 0 0 10px rgba(180,190,200,0.28);
                          pointer-events: auto;
                        }
                        .mg-tiktok-range:active::-webkit-slider-thumb { cursor: grabbing; }
                      `}</style>

                      <div style={{ position: "relative", height: 18 }}>
                        <div style={{ position: "absolute", left: 0, right: 0, top: 8, height: 3, background: "rgba(255,255,255,0.09)", borderRadius: 999, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03)" }} />
                        <div
                          style={{
                            position: "absolute",
                            left: `${(100 * ttWinStart) / (lineChartData.length - 1)}%`,
                            width: `${
                              (100 * (ttWinEnd - ttWinStart)) /
                              Math.max(1, lineChartData.length - 1)
                            }%`,
                            top: 8,
                            height: 3,
                            background: "#9aa3ad",
                            boxShadow: "0 0 14px rgba(180,190,200,0.22)",
                            borderRadius: 999,
                          }}
                        />
                        <input
                          className="mg-tiktok-range"
                          type="range"
                          min={0}
                          max={Math.max(0, lineChartData.length - 1)}
                          value={ttWinStart}
                          onChange={(e) =>
                            setTtWinStart(
                              Math.min(
                                parseInt(e.target.value, 10),
                                ttWinEnd - 1
                              )
                            )
                          }
                          style={{ position: "absolute", left: 0, top: 0, width: "100%", height: 18, background: "transparent", margin: 0, pointerEvents: "none" }}
                        />
                        <input
                          className="mg-tiktok-range"
                          type="range"
                          min={0}
                          max={Math.max(0, lineChartData.length - 1)}
                          value={ttWinEnd}
                          onChange={(e) =>
                            setTtWinEnd(
                              Math.max(
                                parseInt(e.target.value, 10),
                                ttWinStart + 1
                              )
                            )
                          }
                          style={{ position: "absolute", left: 0, top: 0, width: "100%", height: 18, background: "transparent", margin: 0, pointerEvents: "none" }}
                        />
                      </div>
                    </div>
                  )}

                  {ChartGameLabels()}
                </div>
              )}
              <button
                type="button"
                aria-label="Resize TikTok chart"
                title="Resize chart"
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  event.preventDefault();
                  chartResizeRef.current = { pointerId: event.pointerId, y: layoutCoordinate(event.clientY), height: chartHeight };
                  event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerMove={(event) => {
                  const start = chartResizeRef.current;
                  if (!start || start.pointerId !== event.pointerId) return;
                  setChartHeight(Math.max(300, Math.min(780, start.height + layoutCoordinate(event.clientY) - start.y)));
                }}
                onPointerUp={(event) => {
                  chartResizeRef.current = null;
                  if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                }}
                onLostPointerCapture={() => { chartResizeRef.current = null; }}
                onKeyDown={(event) => {
                  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                    event.preventDefault();
                    setChartHeight((height) => Math.max(300, Math.min(780, height + (event.key === "ArrowDown" ? 20 : -20))));
                  }
                }}
                style={{ position: "absolute", right: 3, bottom: 3, zIndex: 10030, display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, padding: 0, background: "transparent", border: "none", color: "#8a96a3", cursor: "ns-resize", touchAction: "none" }}
              >
                <Grip size={14} aria-hidden="true" />
              </button>
            </div>
          )}

          {(datasetError || preparationError) && <div role="alert" style={{ padding: 12, color: "#ff9b9b" }}>
            TikTok data could not refresh: {datasetError || preparationError}. {games.length > 0 && "Showing the last loaded publication."}
            <button type="button" onClick={refreshTikTokData}>Retry TikTok data</button>
          </div>}
          {publicationStale && <div role="alert" style={{ padding: 12, color: "#f5e6c8" }}>
            TikTok publication is stale. Showing the last good data, published {manifestGeneratedAt}.
            <button type="button" onClick={refreshTikTokData}>Check for updates</button>
          </div>}
          {Object.entries(historyErrors).filter(([id, message]) => message &&
            comparisonGameObjects.some(game => String(game.config_id) === id)).map(([id, message]) =>
            <div role="alert" key={id} style={{ padding: 12, color: "#ff9b9b" }}>
              History unavailable for {comparisonGameObjects.find(game => String(game.config_id) === id)?.displayName}: {message}
              <button type="button" onClick={() => setHistoryRetry(value => value + 1)}>Retry history</button>
            </div>)}
          {!initialDashboardLoading && <div className={`mg-tiktok-ranking-header mg-dashboard-content${viewMode === "market" ? " mg-tiktok-ranking-header-market" : ""}${viewMode === "table" || showTableView ? " mg-tiktok-ranking-header-table" : ""}`}>
          <MGSectionHeader index={showInsights && comparisonGames.length ? "02" : "01"} accent="paper">
            {viewMode === "market" ? (
              <>
                {marketShape === "opportunity" ? (
                  <span className="tt-opportunity-fixed-window">EXPERIMENTAL · FIXED 7D WINDOW</span>
                ) : (
                  <>
                    <MGHeadingToggle label="TikTok ranking period" value={rankingWindow} onChange={setRankingWindow} angledSelection
                      options={[{ value: "latest", label: "All-time" }, { value: "1d", label: "1 Day" }, { value: "7d", label: "7 Day" }]} />
                    <span className="mg-tiktok-ranking-divider" aria-hidden="true" />
                    <MGHeadingToggle label="Market Mind sort by" value={marketSort} onChange={setMarketSort} angledSelection
                      options={[{ value: "views", label: "Views" }, { value: "growth", label: "Growth %" }]} />
                  </>
                )}
                <span className="mg-tiktok-ranking-divider" aria-hidden="true" />
                <MGHeadingToggle label="Market Mind visualization type" value={marketShape} onChange={setMarketShape} angledSelection
                  options={[{ value: "bubbles", label: "Circles" }, { value: "squares", label: "Squares" },
                    { value: "opportunity", label: "Opportunity Map" }]} />
              </>
            ) : (
              <>
                <MGHeadingToggle label="TikTok ranking period" value={rankingWindow} onChange={setRankingWindow} angledSelection
                  options={[{ value: "latest", label: "All-time" }, { value: "1d", label: "1 Day" }, { value: "7d", label: "7 Day" }]} />
                <span className="mg-tiktok-ranking-divider" aria-hidden="true" />
                <MGHeadingToggle label="TikTok ranking metric" value={sortMode} onChange={setSortMode} angledSelection
                  options={[{ value: "views", label: "Views" }, { value: "videos", label: "Videos" },
                    { value: "momentum", label: "Momentum" }]} />
              </>
            )}
          </MGSectionHeader>
          {viewMode === "market" && (hasActiveFilters || comparisonGames.length > 0) && (
            <button type="button" className="mm-split-toggle" aria-pressed={marketSplitView}
              onClick={() => setMarketSplitView(value => !value)}>
              {marketSplitView ? "Unsplit" : "Split"}
            </button>
          )}
          <details className="mg-tiktok-ranking-info" onKeyDown={event => {
            if (event.key === "Escape") {
              event.currentTarget.open = false;
              event.currentTarget.querySelector("summary").focus();
            }
          }}>
            <summary aria-label={viewMode === "market" ? "Market Mind information" : "TikTok ranking information"}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" />
                <path d="M12 11v6M12 7v1" />
              </svg>
            </summary>
            <div className="mg-tiktok-ranking-context">
            <p>
            {viewMode === "market" ? (
              marketShape === "opportunity"
                ? "Opportunity Map uses a fixed seven-day window to compare observed view growth and video activity."
                : `${marketSort === "growth" ? "Growth percentage" : "Views"} uses the selected period; Circles and Squares show relative scale, while Opportunity Map uses a fixed seven-day window.`
            ) : (
              <>
                {sortMode === "momentum" ? "Momentum ranks use observed seven-day views and video growth; unranked games appear last · one top-ranked hashtag per game" :
                  rankingWindow === "latest" ? "Latest cumulative totals · one hashtag per game, ranked by the selected metric" :
                  sortMode === "videos"
                    ? "Net video-count change between Pacific calendar dates · removals reduce the net total · one hashtag per game"
                    : "Observed view gains between Pacific calendar dates · no estimates · one hashtag per game"}
                {rankingWindow === "7d" && " · shorter histories are labeled partial"}
                {manifestGeneratedAt && <span style={{ display: "block", marginTop: 4 }}>Published {manifestGeneratedAt}</span>}
              </>
            )}
            </p>
            </div>
          </details>
          </div>}
          {initialDashboardLoading ? (
            <DashboardLoader label="TikTok" />
          ) : <div className="tt-ranking-content">
          {viewMode === "market" ? (
            <TikTokMarketMind
              games={marketBaseGames}
              filtered={displayed}
              comparisonGames={comparisonGames}
              setComparisonGames={setComparisonGames}
              onToggleComparisonGame={toggleComparisonGame}
              setShowInsights={setShowInsights}
              splitView={marketSplitView}
              onSplitChange={setMarketSplitView}
              hasActiveFilters={hasActiveFilters}
              growthWindow={rankingWindow === "1d" ? "1d" : "7d"}
              rankingWindow={rankingWindow}
              metric={sortMode === "momentum" ? "views" : sortMode}
              shape={marketShape}
              onShapeChange={setMarketShape}
              sort={marketSort}
              loading={false}
            />
          ) : viewMode === "table" || showTableView ? (
            <VirtualizedTable
              globalLoading={false}
              loading={false}
              skeletonRowCount={skeletonRowCount}
              variant="tiktok"
              rankingMetric={sortMode}
              rankingWindow={rankingWindow}
              filtered={displayed}
              totalViews={totalViews}
              comparisonGames={comparisonGames}
              setComparisonGames={setComparisonGames}
              onToggleComparisonGame={toggleComparisonGame}
              setShowInsights={setShowInsights}
            />
          ) : (
            <VirtualizedTikTokGrid
              games={displayed}
              loading={false}
              setShowInsights={setShowInsights}
              comparisonGames={comparisonGames}
              setComparisonGames={setComparisonGames}
              onToggleComparisonGame={toggleComparisonGame}
              onRemoveGame={handleRemove}
            />
          )}
          {!globalLoading && !loading && !displayed.length && !datasetError && !preparationError &&
            <p role="status">No hashtags match these filters with an available {rankingWindow === "latest" ? "latest snapshot" : `${rankingWindow.toUpperCase()} baseline`}.</p>}
          </div>}
        </div>
      </div>}
    </div>
  );
}

export default TikTok;